import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';

const categoryQuickLinks = [
  { name: 'Shoes', url: 'https://kaspreadsheet.com/category-911-Shoes.html' },
  { name: 'Hoodies/Sweaters', url: 'https://kaspreadsheet.com/search-912/name/Shoes.html' },
  { name: 'T-Shirts', url: 'https://kaspreadsheet.com/search-913/name/Shoes.html' },
  { name: 'Jackets', url: 'https://kaspreadsheet.com/search-914/name/Shoes.html' },
  { name: 'Pants/Shorts', url: 'https://kaspreadsheet.com/search-915/name/Shoes.html' },
  { name: 'Headwear', url: 'https://kaspreadsheet.com/search-916/name/Shoes.html' },
  { name: 'Acessórios', url: 'https://kaspreadsheet.com/search-920/name/Shoes.html' },
  { name: 'Bags', url: 'https://kaspreadsheet.com/search-921/name/Shoes.html' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = useCallback((sectionId: string) => {
    const doScroll = () => {
      const el = document.getElementById(sectionId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(doScroll, 150);
    } else {
      doScroll();
    }
  }, [location.pathname, navigate]);

  const handleSectionClick = (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    e.preventDefault();
    scrollToSection(sectionId);
  };

  const closeMobile = () => setMobileOpen(false);

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
          : 'bg-background-50/70 backdrop-blur-md'
      }`}
    >
      <div className="px-4 md:px-8 py-3 md:py-4 flex items-center justify-between">
        {/* Logo */}
        <div className="flex flex-col flex-shrink-0">
          <Link to="/" className="text-lg md:text-xl font-heading font-bold text-foreground-950 whitespace-nowrap cursor-pointer">
            CSSBuy Spreadsheets
          </Link>
          <span className="text-xs text-foreground-500 hidden md:block">
            Produtos da China 2026
          </span>
        </div>

        {/* Desktop nav links */}
        <div className="hidden md:flex items-center gap-5">
          <Link to="/" className="text-sm font-medium text-foreground-800 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">
            Início
          </Link>
          <a
            href="/#categorias"
            onClick={(e) => handleSectionClick(e, 'categorias')}
            className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Categorias
          </a>
          <a
            href="/#artigos"
            onClick={(e) => handleSectionClick(e, 'artigos')}
            className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Artigos
          </a>
          <a
            href="/#guia-compras"
            onClick={(e) => handleSectionClick(e, 'guia-compras')}
            className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Guia de Compras
          </a>
        </div>

        {/* Desktop CTA */}
        <div className="hidden md:flex items-center gap-3">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            cssbuy sheet
            <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center" />
          </a>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-accent-500 text-background-50 rounded-full text-sm font-semibold hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            buy it now
            <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center" />
          </a>
        </div>

        {/* Mobile menu button */}
        <button
          className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-900 cursor-pointer rounded-lg hover:bg-background-200 transition-colors"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Menu"
        >
          <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`} />
        </button>
      </div>

      {/* Mobile menu panel */}
      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 shadow-lg">
          <div className="px-4 py-4 flex flex-col gap-1 max-h-[70vh] overflow-y-auto">
            {/* Main links */}
            <Link to="/" onClick={closeMobile} className="px-4 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-xl transition-colors whitespace-nowrap flex items-center gap-3 cursor-pointer">
              <i className="ri-home-4-line w-5 h-5 flex items-center justify-center text-primary-500" />
              Início
            </Link>
            <a
              href="/#categorias"
              onClick={(e) => { e.preventDefault(); closeMobile(); scrollToSection('categorias'); }}
              className="px-4 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-xl transition-colors whitespace-nowrap flex items-center gap-3 cursor-pointer"
            >
              <i className="ri-grid-line w-5 h-5 flex items-center justify-center text-primary-500" />
              Categorias
            </a>
            <a
              href="/#artigos"
              onClick={(e) => { e.preventDefault(); closeMobile(); scrollToSection('artigos'); }}
              className="px-4 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-xl transition-colors whitespace-nowrap flex items-center gap-3 cursor-pointer"
            >
              <i className="ri-article-line w-5 h-5 flex items-center justify-center text-primary-500" />
              Artigos SEO
            </a>
            <a
              href="/#guia-compras"
              onClick={(e) => { e.preventDefault(); closeMobile(); scrollToSection('guia-compras'); }}
              className="px-4 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-xl transition-colors whitespace-nowrap flex items-center gap-3 cursor-pointer"
            >
              <i className="ri-shopping-bag-3-line w-5 h-5 flex items-center justify-center text-primary-500" />
              Guia de Compras
            </a>

            {/* Divider */}
            <div className="border-t border-background-200 my-2" />

            {/* Category quick links */}
            <p className="px-4 py-1 text-xs font-semibold text-foreground-400 uppercase tracking-wider">
              Categorias Rápidas
            </p>
            {categoryQuickLinks.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                onClick={closeMobile}
                className="px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-xl transition-colors whitespace-nowrap flex items-center gap-3 cursor-pointer"
              >
                <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center text-foreground-400" />
                {cat.name}
              </a>
            ))}

            {/* Divider */}
            <div className="border-t border-background-200 my-2" />

            {/* CTA buttons */}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={closeMobile}
              className="flex items-center justify-center gap-2 px-5 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              cssbuy sheet
              <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center" />
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={closeMobile}
              className="flex items-center justify-center gap-2 px-5 py-3 bg-accent-500 text-background-50 rounded-full text-sm font-semibold hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              buy it now
              <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center" />
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}